<template>
  <div>
    <loading-overlay v-if="showLoadingOverlay"></loading-overlay>
    <help-hub-header
      v-if="routeMeta && routeMeta.displayHeader &&!isHelpHubHost"
      :env="appEnv"
      @on-header-success="onHHComponentSuccess"
      @on-header-error="onHHComponentError"
      @on-user-country-updated="onUserCountryUpdated"
      :hide-search="routeMeta.hideSearch || undefined"
      :title="routeMeta.title"
      ref="header"
    ></help-hub-header>
    <div v-if="routeMeta.displayHeader &&!isHelpHubHost">
      <transition :name="validateTransition" mode="out-in">
        <router-view v-if="authResponse"></router-view>
      </transition>
    </div>
    <div v-else-if="!isHelpHubHost">
      <transition name="fade" mode="out-in">
        <router-view></router-view>
      </transition>
    </div>
    <ModalsContainer />
    <help-hub-footer
      v-if="displayFooter"
      :env="appEnv"
      v-show="!isLoading&&!isCallback"
      :platform-category="platformCategory"
      ref="footerWebComponent"
    />
  </div>
</template>

<script>

import { getCurrentInstance } from 'vue';
import LoadingOverlay from "@/components/LoadingOverlay";
import axios from "axios";
import { mapGetters, mapMutations } from "vuex";
import * as CONSTANTS from "@/store/constants";
import api from "@/api";
import EmployeeHubRedirect from "./components/homepage/EmployeeHubRedirect";
import {safeImportComponent} from "./utils/componentImportUtils";
import { ModalsContainer } from "vue-final-modal";

export default {
  name: "app",
  data() {
    return {
      appEnv: CURRENT_ENV,
      showFooter: false,
      appInstance: null,
      localIsLoading: true  // Local state to avoid Vuex reactivity issues in compat mode
    };
  },
  computed: {
    isCallback() {
      if (this.$route.path === "/callback") {
        return true;
      }
      return false;
    },
    isHelpHubHost() {
      if (
        window.location.href === HELP_HUB_DOMAIN ||
        window.location.href === HELP_HUB_DOMAIN + "home" ||
        window.location.href === EMPLOYEE_HUB_DOMAIN ||
        window.location.href === EMPLOYEE_HUB_DOMAIN + "home"
      ) {
        window.location = ADSK_ONE_BASE_URL;
      } else {
        return false;
      }
    },
    ...mapGetters(["authResponse", "isLoading", "country", "searchReady"]),
    validateTransition() {
      return this.$route.name === "knowledge-article" ||
        this.$route.name === "knowledge-articles-by-id"
        ? ""
        : "fade";
    },
    routeMeta() {
      return this.$route.meta;
    },
    displayFooter() {
      return this.showFooter;
    },
    platformCategory() {
      if (
        this.$route &&
        this.$route.path &&
        this.$route.path.includes(`/${CONSTANTS.HR_PLATFORM}/`)
      ) {
        return CONSTANTS.HR_PLATFORM;
      } else {
        return this.$route.meta.showITAndHR ? "ALL" : CONSTANTS.IT_PLATFORM;
      }
    },
    showValidation() {
      if (
        this.$route.name === "knowledge-articles" ||
        this.$route.name === "knowledge-articles-by-id"
      ) {
        return true;
      } else {
        return !this.isLoading && !this.isCallback;
      }
    },
    shouldShowLoadingOverlay() {
      const result = (this.isLoading && !this.isHelpHubHost) || this.isCallback;
      return result;
    },
    showLoadingOverlay() {
      const result = (this.localIsLoading || this.isLoading) && !this.isHelpHubHost;
      return result;
    }
  },
  components: {
    EmployeeHubRedirect,
    LoadingOverlay,
    ModalsContainer
  },
  created() {
    // Capture app instance for Vue 3 global properties
    const instance = getCurrentInstance();
    if (instance) {
      this.appInstance = instance.appContext.app;
    }

    this.checkIfRequestisFromSAMLError();
    // If there is no header, we can't expect an auth response so just stop loading right away
    if (!this.routeMeta.displayHeader) {
      console.log('[App.vue] No header expected, stopping loading');
      this.localIsLoading = false;
      this.setIsLoading(false);
    } 
  },
  mounted() {
    
    // Debug: Check if web component exists and add event listeners
    this.$nextTick(() => {
      const headerElement = this.$refs.header;

      // Also check the actual DOM element
      const domHeaderElement = document.querySelector('help-hub-header');

      if (headerElement) {
        // Create a proxy to intercept ALL events
        const originalAddEventListener = headerElement.addEventListener.bind(headerElement);
        const eventLog = [];

        // Catch ALL events on the element
        const observer = new MutationObserver(() => {
        });
        observer.observe(headerElement, { attributes: true, childList: true, subtree: true });

        // Listen for common event names (both camelCase and kebab-case)
        const eventNames = [
          'onHHComponentSuccess',
          'onhhcomponentsuccess',
          'HHComponentSuccess',
          'hhcomponentsuccess',
          'onHHComponentError',
          'onhhcomponenterror',
          'HHComponentError',
          'hhcomponenterror',
          'onUserCountryUpdated',
          'onusercountryupdated',
          'UserCountryUpdated',
          'usercountryupdated'
        ];

        eventNames.forEach(eventName => {
          headerElement.addEventListener(eventName, (event) => {
            console.log(`[App.vue] 🔔 Event "${eventName}" caught!`, event);

            // Try to handle the event
            if (eventName.toLowerCase().includes('success')) {
              this.onHHComponentSuccess(event);
            } else if (eventName.toLowerCase().includes('error')) {
              this.onHHComponentError(event);
            } else if (eventName.toLowerCase().includes('country')) {
              this.onUserCountryUpdated(event);
            }
          });
        });

        // Try to access the Vue instance inside the web component
        setTimeout(() => {
          // Try to access Vue 3 component instance
          if (headerElement.__vueParentComponent) {
            const component = headerElement.__vueParentComponent;
            
            // Try to monkey-patch the emit function to intercept events
            if (component.emit) {
              const originalEmit = component.emit.bind(component);
              component.emit = (event, ...args) => {
                console.log(`[App.vue] 🎯 Component emitting event: "${event}"`, args);

                // Call our handlers
                if (event === 'onHHComponentSuccess' || event.toLowerCase().includes('success')) {
                  console.log('[App.vue] ✅ Caught success emit, calling handler');
                  this.onHHComponentSuccess(...args);
                }
                if (event === 'onHHComponentError' || event.toLowerCase().includes('error')) {
                  console.log('[App.vue] ❌ Caught error emit, calling handler');
                  this.onHHComponentError(...args);
                }
                if (event === 'onUserCountryUpdated' || event.toLowerCase().includes('country')) {
                  console.log('[App.vue] 🌍 Caught country emit, calling handler');
                  this.onUserCountryUpdated(...args);
                }

                // Call original emit
                return originalEmit(event, ...args);
              };
            }
          }
        }, 1000);
      } else {
        console.warn('[App.vue] ⚠️ Header element NOT found in $refs');
      }

    });
  },
  methods: {
    ...mapMutations(["setAuthResponse", "setIsLoading", "setCountry"]),
    debugLoadingCondition() {
      const result = this.localIsLoading && !this.isHelpHubHost;
      console.log('[App.vue] debugLoadingCondition() - localIsLoading:', this.localIsLoading, 'isHelpHubHost:', this.isHelpHubHost, 'result:', result);
      return result;
    },
    checkIfRequestisFromSAMLError() {
      if (this.$route.query && this.$route.query.error == "access_denied") {
        this.$router.push({ name: "login-error" });
      }
    },
    onUserCountryUpdated(response) {
      console.log('[App.vue] onUserCountryUpdated', response);
      if (response && response.detail) {
        this.setCountry(response.detail[0].countryPreferences);
      }
    },
    onHHComponentSuccess(response) {
      // Unwrap response.detail like the working Vue 3 project
      response = response.detail;

      if(window.location.href!==HELP_HUB_DOMAIN && window.location.href!==HELP_HUB_DOMAIN+"home") {
        safeImportComponent(HH_COMPONENTS_URL + "/theme.css");
      }

      axios.defaults.headers.common["Authorization"] =
        "Bearer " + response.accessToken;
      this.setAuthResponse(response);

      this.showFooter = true;
      this.localIsLoading = false;
      this.setIsLoading(false);

      var mixpanelInstance = response.mixpanelInstance;
      // Use Vue 3 app.config.globalProperties instead of Vue.prototype
      if (mixpanelInstance && this.appInstance && !this.appInstance.config.globalProperties.$hhMixpanel) {
        this.appInstance.config.globalProperties.$hhMixpanel = mixpanelInstance;
        mixpanelInstance.addRouterAutoTracking(this.$router);
        api.addInterceptors(mixpanelInstance);
      }
    },
    onHHComponentError(error) {
      console.error('[App.vue] ❌ onHHComponentError - Auth failed!', error);
      this.showFooter = false;
      this.localIsLoading = false;
      this.setIsLoading(false);
      console.log('[App.vue] Loading set to false due to error');
    }
  }
};
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition-duration: 1s;
  transition-property: opacity;
  transition-timing-function: ease;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}
</style>

<style>
html {
  height: 100%;
  box-sizing: border-box;
}
*,
*:before,
*:after {
  box-sizing: inherit;
}
.app-body {
  position: relative;
  margin: 0;
  padding-bottom: 60px;
  min-height: 100%;
}
.app-footer {
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 60px;
  line-height: 60px;
}
.app-footer p {
  max-height: 40px;
}
</style>
